import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Switch,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MemorialServiceInput');

import {
  ChapelDetails,
  ChurchDetails,
  CemeteryDetails,
  ResidenceDetails,
  OutdoorDetails,
  OtherDetails,
  ServiceDetails,
  memorialServiceInfoService
} from '../../../../services/memorialServiceInfo';

type MemorialServiceInputScreenRouteProp = RouteProp<RootStackParamList, 'MemorialServiceInput'>;

type ArrangementType = 'chapel' | 'church' | 'cemetary' | 'residence' | 'outdoors' | 'other' | null;

export const MemorialServiceInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<MemorialServiceInputScreenRouteProp>();

  const { memorialServiceData, isEditing } = route.params || {};

  const [selectedArrangement, setSelectedArrangement] = useState<ArrangementType>(
    memorialServiceData?.arrangementType as ArrangementType || null
  );
  const [specialInstructions, setSpecialInstructions] = useState(
    memorialServiceData?.specialInstructions || ''
  );
  const [saving, setSaving] = useState(false);

  // Initialize details with existing data if editing
  const getInitialDetails = () => {
    if (memorialServiceData?.serviceDetails && memorialServiceData.arrangementType) {
      return memorialServiceData.serviceDetails;
    }
    return null;
  };

  const initialDetails = getInitialDetails();

  // Chapel Details
  const [chapelDetails, setChapelDetails] = useState<ChapelDetails>({
    liveStreamingService: (initialDetails as ChapelDetails)?.liveStreamingService || false,
    viewingBeforeService: (initialDetails as ChapelDetails)?.viewingBeforeService || false,
    receptionAfter: (initialDetails as ChapelDetails)?.receptionAfter || false,
    specificRequests: (initialDetails as ChapelDetails)?.specificRequests || '',
    musicPreferences: (initialDetails as ChapelDetails)?.musicPreferences || '',
  });

  // Church Details
  const [churchDetails, setChurchDetails] = useState<ChurchDetails>({
    typeOfMusic: (initialDetails as ChurchDetails)?.typeOfMusic || '',
    specificClergy: (initialDetails as ChurchDetails)?.specificClergy || '',
    communion: (initialDetails as ChurchDetails)?.communion || false,
    religiousReadings: (initialDetails as ChurchDetails)?.religiousReadings || '',
    denominationRequirements: (initialDetails as ChurchDetails)?.denominationRequirements || '',
  });

  // Cemetery Details
  const [cemeteryDetails, setCemeteryDetails] = useState<CemeteryDetails>({
    outdoorTent: (initialDetails as CemeteryDetails)?.outdoorTent || false,
    chairsProvided: (initialDetails as CemeteryDetails)?.chairsProvided || false,
    typeOfCeremony: (initialDetails as CemeteryDetails)?.typeOfCeremony || '',
    weatherContingency: (initialDetails as CemeteryDetails)?.weatherContingency || '',
    gravesideMusic: (initialDetails as CemeteryDetails)?.gravesideMusic || false,
  });

  // Residence Details
  const [residenceDetails, setResidenceDetails] = useState<ResidenceDetails>({
    homeLocation: (initialDetails as ResidenceDetails)?.homeLocation || '',
    maxGuestCount: (initialDetails as ResidenceDetails)?.maxGuestCount || '',
    cateringNeeds: (initialDetails as ResidenceDetails)?.cateringNeeds || false,
    parkingArrangements: (initialDetails as ResidenceDetails)?.parkingArrangements || '',
    soundSystemNeeded: (initialDetails as ResidenceDetails)?.soundSystemNeeded || false,
  });

  // Outdoor Details
  const [outdoorDetails, setOutdoorDetails] = useState<OutdoorDetails>({
    specificLocation: (initialDetails as OutdoorDetails)?.specificLocation || '',
    permitRequired: (initialDetails as OutdoorDetails)?.permitRequired || false,
    weatherBackupPlan: (initialDetails as OutdoorDetails)?.weatherBackupPlan || '',
    seatingArrangements: (initialDetails as OutdoorDetails)?.seatingArrangements || '',
    accessibilityConsiderations: (initialDetails as OutdoorDetails)?.accessibilityConsiderations || '',
  });

  // Other Details
  const [otherDetails, setOtherDetails] = useState<OtherDetails>({
    customDescription: (initialDetails as OtherDetails)?.customDescription || '',
    specialVenue: (initialDetails as OtherDetails)?.specialVenue || '',
    uniqueRequirements: (initialDetails as OtherDetails)?.uniqueRequirements || '',
    logisticalNeeds: (initialDetails as OtherDetails)?.logisticalNeeds || '',
  });

  const handleSave = async () => {
    if (!selectedArrangement) {
      Alert.alert('Error', 'Please select a memorial service type');
      return;
    }

    try {
      setSaving(true);

      // Determine which service details to save based on selected type
      let serviceDetails: ServiceDetails = null;

      switch (selectedArrangement) {
        case 'chapel':
          serviceDetails = chapelDetails;
          break;
        case 'church':
          serviceDetails = churchDetails;
          break;
        case 'cemetary':
          serviceDetails = cemeteryDetails;
          break;
        case 'residence':
          serviceDetails = residenceDetails;
          break;
        case 'outdoors':
          serviceDetails = outdoorDetails;
          break;
        case 'other':
          serviceDetails = otherDetails;
          break;
      }

      // Prepare the data to save
      const memorialServiceUpdateData = {
        serviceType: selectedArrangement,
        serviceDetails,
        specificInstructions: specialInstructions || undefined,
      };

      let savedMemorialService;

      if (isEditing && memorialServiceData?.id) {
        // Update existing record
        savedMemorialService = await memorialServiceInfoService.updateMemorialServiceInfo(
          memorialServiceData.id,
          memorialServiceUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedMemorialService?.data || savedMemorialService;
        logger.debug('Memorial service updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('MemorialServiceLockUnlock', { memorialServiceId: updatedData.id });
      } else {
        // Create new record
        savedMemorialService = await memorialServiceInfoService.createMemorialServiceInfo(memorialServiceUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedMemorialService?.data || savedMemorialService;
        logger.debug('Memorial service saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('MemorialServiceLockUnlock', { memorialServiceId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving memorial service:', error);
      Alert.alert('Error', 'Failed to save memorial service information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderDetailsInput = () => {
    switch (selectedArrangement) {
      case 'chapel':
        return (
          <View>
            <Text style={styles.detailsTitle}>Funeral Home Chapel Details:</Text>

            {/* Live Streaming Service Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Live streaming service?</Text>
              <Switch
                value={chapelDetails.liveStreamingService}
                onValueChange={(value) =>
                  setChapelDetails({ ...chapelDetails, liveStreamingService: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={chapelDetails.liveStreamingService ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Viewing Before Service Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Viewing before service?</Text>
              <Switch
                value={chapelDetails.viewingBeforeService}
                onValueChange={(value) =>
                  setChapelDetails({ ...chapelDetails, viewingBeforeService: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={chapelDetails.viewingBeforeService ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Reception After Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Reception after service?</Text>
              <Switch
                value={chapelDetails.receptionAfter}
                onValueChange={(value) =>
                  setChapelDetails({ ...chapelDetails, receptionAfter: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={chapelDetails.receptionAfter ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Music Preferences Input */}
            <Text style={styles.inputLabel}>Music preferences:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your preferred music for the service..."
              value={chapelDetails.musicPreferences}
              onChangeText={(value) =>
                setChapelDetails({ ...chapelDetails, musicPreferences: value })
              }
              multiline
            />

            {/* Specific Requests Input */}
            <Text style={styles.inputLabel}>Any specific requests for the chapel service?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe any specific requests or requirements..."
              value={chapelDetails.specificRequests}
              onChangeText={(value) =>
                setChapelDetails({ ...chapelDetails, specificRequests: value })
              }
              multiline
            />
          </View>
        );
      case 'church':
        return (
          <View>
            <Text style={styles.detailsTitle}>Place of Worship Details:</Text>

            {/* Type of Music Input */}
            <Text style={styles.inputLabel}>Type of music?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe the type of music you prefer (hymns, contemporary, etc.)..."
              value={churchDetails.typeOfMusic}
              onChangeText={(value) =>
                setChurchDetails({ ...churchDetails, typeOfMusic: value })
              }
              multiline
            />

            {/* Specific Clergy Input */}
            <Text style={styles.inputLabel}>Specific clergy?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Name or description of preferred clergy member..."
              value={churchDetails.specificClergy}
              onChangeText={(value) =>
                setChurchDetails({ ...churchDetails, specificClergy: value })
              }
              multiline
            />

            {/* Communion Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Communion?</Text>
              <Switch
                value={churchDetails.communion}
                onValueChange={(value) =>
                  setChurchDetails({ ...churchDetails, communion: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={churchDetails.communion ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Religious Readings Input */}
            <Text style={styles.inputLabel}>Religious readings or scriptures:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Specify any preferred religious readings or scriptures..."
              value={churchDetails.religiousReadings}
              onChangeText={(value) =>
                setChurchDetails({ ...churchDetails, religiousReadings: value })
              }
              multiline
            />

            {/* Denomination Requirements Input */}
            <Text style={styles.inputLabel}>Denomination requirements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any specific denomination requirements or traditions..."
              value={churchDetails.denominationRequirements}
              onChangeText={(value) =>
                setChurchDetails({ ...churchDetails, denominationRequirements: value })
              }
              multiline
            />
          </View>
        );
      case 'cemetary':
        return (
          <View>
            <Text style={styles.detailsTitle}>Graveside Service Details:</Text>

            {/* Outdoor Tent Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Outdoor tent?</Text>
              <Switch
                value={cemeteryDetails.outdoorTent}
                onValueChange={(value) =>
                  setCemeteryDetails({ ...cemeteryDetails, outdoorTent: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={cemeteryDetails.outdoorTent ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Chairs Provided Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Chairs provided?</Text>
              <Switch
                value={cemeteryDetails.chairsProvided}
                onValueChange={(value) =>
                  setCemeteryDetails({ ...cemeteryDetails, chairsProvided: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={cemeteryDetails.chairsProvided ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Graveside Music Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Graveside music?</Text>
              <Switch
                value={cemeteryDetails.gravesideMusic}
                onValueChange={(value) =>
                  setCemeteryDetails({ ...cemeteryDetails, gravesideMusic: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={cemeteryDetails.gravesideMusic ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Type of Ceremony Input */}
            <Text style={styles.inputLabel}>Type of ceremony?</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe the type of graveside ceremony you prefer..."
              value={cemeteryDetails.typeOfCeremony}
              onChangeText={(value) =>
                setCemeteryDetails({ ...cemeteryDetails, typeOfCeremony: value })
              }
              multiline
            />

            {/* Weather Contingency Input */}
            <Text style={styles.inputLabel}>Weather contingency plan:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Plans for inclement weather during the service..."
              value={cemeteryDetails.weatherContingency}
              onChangeText={(value) =>
                setCemeteryDetails({ ...cemeteryDetails, weatherContingency: value })
              }
              multiline
            />
          </View>
        );
      case 'residence':
        return (
          <View>
            <Text style={styles.detailsTitle}>Private Residence Details:</Text>

            {/* Home Location Input */}
            <Text style={styles.inputLabel}>Home location:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Address or description of the home location..."
              value={residenceDetails.homeLocation}
              onChangeText={(value) =>
                setResidenceDetails({ ...residenceDetails, homeLocation: value })
              }
              multiline
            />

            {/* Max Guest Count Input */}
            <Text style={styles.inputLabel}>Maximum guest count:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Estimated maximum number of guests the space can accommodate..."
              value={residenceDetails.maxGuestCount}
              onChangeText={(value) =>
                setResidenceDetails({ ...residenceDetails, maxGuestCount: value })
              }
            />

            {/* Catering Needs Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Catering needs?</Text>
              <Switch
                value={residenceDetails.cateringNeeds}
                onValueChange={(value) =>
                  setResidenceDetails({ ...residenceDetails, cateringNeeds: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={residenceDetails.cateringNeeds ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Sound System Needed Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Sound system needed?</Text>
              <Switch
                value={residenceDetails.soundSystemNeeded}
                onValueChange={(value) =>
                  setResidenceDetails({ ...residenceDetails, soundSystemNeeded: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={residenceDetails.soundSystemNeeded ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Parking Arrangements Input */}
            <Text style={styles.inputLabel}>Parking arrangements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe parking availability and arrangements for guests..."
              value={residenceDetails.parkingArrangements}
              onChangeText={(value) =>
                setResidenceDetails({ ...residenceDetails, parkingArrangements: value })
              }
              multiline
            />
          </View>
        );
      case 'outdoors':
        return (
          <View>
            <Text style={styles.detailsTitle}>Outdoor Location Details:</Text>

            {/* Specific Location Input */}
            <Text style={styles.inputLabel}>Specific location:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Park, garden, beach, or other outdoor location description..."
              value={outdoorDetails.specificLocation}
              onChangeText={(value) =>
                setOutdoorDetails({ ...outdoorDetails, specificLocation: value })
              }
              multiline
            />

            {/* Permit Required Toggle */}
            <View style={styles.toggleRow}>
              <Text style={styles.toggleLabel}>Permit required?</Text>
              <Switch
                value={outdoorDetails.permitRequired}
                onValueChange={(value) =>
                  setOutdoorDetails({ ...outdoorDetails, permitRequired: value })
                }
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={outdoorDetails.permitRequired ? '#FFFFFF' : '#FFFFFF'}
              />
            </View>

            {/* Weather Backup Plan Input */}
            <Text style={styles.inputLabel}>Weather backup plan:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Alternative indoor location or plan for bad weather..."
              value={outdoorDetails.weatherBackupPlan}
              onChangeText={(value) =>
                setOutdoorDetails({ ...outdoorDetails, weatherBackupPlan: value })
              }
              multiline
            />

            {/* Seating Arrangements Input */}
            <Text style={styles.inputLabel}>Seating arrangements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Description of seating setup for the outdoor service..."
              value={outdoorDetails.seatingArrangements}
              onChangeText={(value) =>
                setOutdoorDetails({ ...outdoorDetails, seatingArrangements: value })
              }
              multiline
            />

            {/* Accessibility Considerations Input */}
            <Text style={styles.inputLabel}>Accessibility considerations:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Accessibility needs for elderly or disabled guests..."
              value={outdoorDetails.accessibilityConsiderations}
              onChangeText={(value) =>
                setOutdoorDetails({ ...outdoorDetails, accessibilityConsiderations: value })
              }
              multiline
            />
          </View>
        );
      case 'other':
        return (
          <View>
            <Text style={styles.detailsTitle}>Other Memorial Service Details:</Text>

            {/* Custom Description Input */}
            <Text style={styles.inputLabel}>Custom description:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Describe your custom memorial service arrangement..."
              value={otherDetails.customDescription}
              onChangeText={(value) =>
                setOtherDetails({ ...otherDetails, customDescription: value })
              }
              multiline
            />

            {/* Special Venue Input */}
            <Text style={styles.inputLabel}>Special venue:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Unique venue or location for the memorial service..."
              value={otherDetails.specialVenue}
              onChangeText={(value) =>
                setOtherDetails({ ...otherDetails, specialVenue: value })
              }
              multiline
            />

            {/* Unique Requirements Input */}
            <Text style={styles.inputLabel}>Unique requirements:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Any unique or special requirements for your memorial service..."
              value={otherDetails.uniqueRequirements}
              onChangeText={(value) =>
                setOtherDetails({ ...otherDetails, uniqueRequirements: value })
              }
              multiline
            />

            {/* Logistical Needs Input */}
            <Text style={styles.inputLabel}>Logistical needs:</Text>
            <TextInput
              style={styles.structuredInput}
              placeholder="Special logistical arrangements or coordination needs..."
              value={otherDetails.logisticalNeeds}
              onChangeText={(value) =>
                setOtherDetails({ ...otherDetails, logisticalNeeds: value })
              }
              multiline
            />
          </View>
        );
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Where do you want your funeral to take place?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('chapel')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'chapel' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Funeral Home Chapel</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('church')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'church' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Church</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('cemetary')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'cemetary' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Graveside (Cemetery or Burial Site)</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('outdoors')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'outdoors' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Outdoor Locations</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('residence')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'residence' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Private Residence</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedArrangement('other')}
            >
              <View style={styles.radioButton}>
                {selectedArrangement === 'other' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Other</Text>
            </TouchableOpacity>
          </View>

          {renderDetailsInput()}

          <Text style={styles.label}>Special instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <TouchableOpacity
            style={[styles.saveButton, saving && { opacity: 0.7 }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Save'}</Text>
            )}
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  structuredInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(15),
    minHeight: s(80),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(15),
    paddingVertical: ms(5),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
    marginRight: ms(15),
  },
  inputLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
    marginTop: ms(5),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  bottomPadding: {
    height: s(120),
  },
});

export default MemorialServiceInputScreen;